import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Select, Input } from '../shared';
import { FaPlus, FaTimes } from 'react-icons/fa';

const FieldMappingModal = ({
  isOpen,
  onClose,
  onConfirm,
  incomingFields = [],
  existingFields = [],
  sourceType = 'csv' // 'csv' or 'hubspot'
}) => {
  const [fieldMappings, setFieldMappings] = useState({});
  const [newFields, setNewFields] = useState({});
  const [showNewFieldInput, setShowNewFieldInput] = useState({});

  // Standard fields that can be mapped - different for accounts vs contacts
  const standardFields = useMemo(() => {
    // Determine if this is for contacts or accounts based on incoming fields or a prop
    const isContacts = incomingFields.some(field => 
      field.toLowerCase().includes('firstname') || 
      field.toLowerCase().includes('lastname') ||
      field.toLowerCase().includes('email')
    ) || sourceType === 'contacts';
    
    if (isContacts) {
      return [
        { value: 'firstName', label: 'First Name', required: false },
        { value: 'lastName', label: 'Last Name', required: false },
        { value: 'email', label: 'Email', required: false },
        { value: 'phone', label: 'Phone', required: false },
        { value: 'title', label: 'Job Title', required: false },
        { value: 'companyName', label: 'Company Name', required: false },
        { value: 'linkedinId', label: 'LinkedIn ID', required: false },
        { value: 'source', label: 'Source', required: false },
        { value: 'custom', label: 'Create New Field', required: false }
      ];
    } else {
      // Account fields - all available fields from Company model
      return [
        { value: 'companyName', label: 'Company Name', required: true },
        { value: 'website', label: 'Website', required: false },
        { value: 'summary', label: 'Summary/Description', required: false },
        { value: 'status', label: 'Status', required: false },
        { value: 'contactName', label: 'Contact Name', required: false },
        { value: 'contactEmail', label: 'Contact Email', required: false },
        { value: 'contactPhone', label: 'Contact Phone', required: false },
        { value: 'industry', label: 'Industry', required: false },
        { value: 'companySize', label: 'Company Size', required: false },
        { value: 'dealStage', label: 'Deal Stage', required: false },
        { value: 'lastContactDate', label: 'Last Contact Date', required: false },
        { value: 'notes', label: 'Notes', required: false },
        { value: 'assignedTo', label: 'Assigned To', required: false },
        { value: 'address', label: 'Address', required: false },
        { value: 'source', label: 'Source', required: false },
        { value: 'tags', label: 'Tags', required: false },
        { value: 'hubspotId', label: 'HubSpot ID', required: false },
        { value: 'salesforceId', label: 'Salesforce ID', required: false },
        { value: 'custom', label: 'Create New Field', required: false }
      ];
    }
  }, [incomingFields, sourceType]);

  // Initialize mappings when modal opens
  useEffect(() => {
    if (isOpen && incomingFields.length > 0) {
      const initialMappings = {};
      
      incomingFields.forEach(field => {
        // Try to auto-map based on field name similarity
        const normalizedField = field.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        const autoMapped = standardFields.find(sf => {
          const normalizedStandard = sf.value.toLowerCase().replace(/[^a-z0-9]/g, '');
          return normalizedField.includes(normalizedStandard) || 
                 normalizedStandard.includes(normalizedField) ||
                 (normalizedField === 'name' && sf.value === 'companyName') ||
                 (normalizedField === 'company' && sf.value === 'companyName') ||
                 (normalizedField === 'email' && sf.value === 'contactEmail') ||
                 (normalizedField === 'phone' && sf.value === 'contactPhone');
        });
        
        if (autoMapped && autoMapped.value !== 'custom') {
          initialMappings[field] = autoMapped.value;
        } else if (field.toLowerCase() === 'companyname' || field.toLowerCase() === 'company name') {
          initialMappings[field] = 'companyName';
        }
      });
      
      setFieldMappings(initialMappings);
      setNewFields({});
      setShowNewFieldInput({});
    }
  }, [isOpen, incomingFields, standardFields]);

  const handleMappingChange = (incomingField, targetField) => {
    try {
      if (targetField === 'custom') {
        // Show input for new field name
        setShowNewFieldInput(prev => ({ ...prev, [incomingField]: true }));
        setFieldMappings(prev => ({ ...prev, [incomingField]: 'custom' }));
      } else {
        // Remove from newFields if it was there
        const newNewFields = { ...newFields };
        delete newNewFields[incomingField];
        setNewFields(newNewFields);
        
        setShowNewFieldInput(prev => {
          const updated = { ...prev };
          delete updated[incomingField];
          return updated;
        });
        
        setFieldMappings(prev => ({
          ...prev,
          [incomingField]: targetField
        }));
      }
    } catch (error) {
      console.error('Error in handleMappingChange:', error);
    }
  };

  const handleNewFieldNameChange = (incomingField, newFieldName) => {
    setNewFields(prev => ({
      ...prev,
      [incomingField]: newFieldName.trim()
    }));
  };

  const handleConfirm = () => {
    try {
      // Validate required fields based on type
      const isContacts = standardFields.some(sf => sf.value === 'firstName');
      
      if (!isContacts) {
        // For accounts, companyName is required
        const companyNameMapped = Object.values(fieldMappings).includes('companyName');
        if (!companyNameMapped) {
          alert('Please map at least one field to "Company Name" (required field)');
          return;
        }
      } else {
        // For contacts, at least one of firstName, lastName, or email should be mapped
        const hasRequiredField = Object.values(fieldMappings).some(field => 
          ['firstName', 'lastName', 'email'].includes(field)
        );
        if (!hasRequiredField && incomingFields.length > 0) {
          alert('Please map at least one field to "First Name", "Last Name", or "Email"');
          return;
        }
      }

      // Build the mapping object
      const mappings = {};
      const customFields = {};
      
      Object.entries(fieldMappings).forEach(([incomingField, targetField]) => {
        if (targetField === 'custom') {
          const customFieldName = newFields[incomingField];
          if (customFieldName) {
            customFields[incomingField] = customFieldName;
          }
        } else {
          mappings[incomingField] = targetField;
        }
      });

      onConfirm({
        mappings,
        customFields
      });
    } catch (error) {
      console.error('Error in handleConfirm:', error);
      alert('An error occurred while confirming the mapping. Please try again.');
    }
  };

  const unmappedFields = incomingFields.filter(field => !fieldMappings[field] || fieldMappings[field] === '');

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('FieldMappingModal opened with:', {
        incomingFields,
        incomingFieldsLength: incomingFields.length,
        sourceType,
        standardFieldsCount: standardFields.length
      });
    }
  }, [isOpen, incomingFields, sourceType, standardFields]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Map Fields"
      size="large"
    >
      <div style={{ marginBottom: '20px', color: '#6b7280', fontSize: '14px' }}>
        Map the incoming fields from {sourceType === 'csv' ? 'your CSV file' : 'HubSpot'} to your account fields.
        You can map to existing fields or create new custom fields.
      </div>

      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        {incomingFields.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <p>No fields found to map. Please check your import source.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151', width: '40%' }}>
                  Incoming Field
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151', width: '60%' }}>
                  Map To
                </th>
              </tr>
            </thead>
            <tbody>
              {incomingFields.map((field, index) => {
              const currentMapping = fieldMappings[field];
              const isCustom = currentMapping === 'custom';
              const showInput = showNewFieldInput[field];
              
              return (
                <tr key={field} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', fontSize: '14px', color: '#111827' }}>
                    <strong>{field}</strong>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {showInput ? (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Input
                          placeholder="Enter new field name"
                          value={newFields[field] || ''}
                          onChange={(e) => handleNewFieldNameChange(field, e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => {
                            setShowNewFieldInput(prev => {
                              const updated = { ...prev };
                              delete updated[field];
                              return updated;
                            });
                            setFieldMappings(prev => {
                              const updated = { ...prev };
                              delete updated[field];
                              return updated;
                            });
                          }}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    ) : (
                      <Select
                        value={currentMapping || ''}
                        onChange={(e) => handleMappingChange(field, e.target.value)}
                        options={[
                          { value: '', label: '-- Skip this field --' },
                          ...standardFields.map(sf => ({
                            value: sf.value,
                            label: `${sf.label}${sf.required ? ' (required)' : ''}`
                          }))
                        ]}
                        style={{ width: '100%' }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        )}

        {incomingFields.length > 0 && unmappedFields.length > 0 && (
          <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '14px', color: '#92400e' }}>
            <strong>Note:</strong> {unmappedFields.length} field{unmappedFields.length !== 1 ? 's' : ''} will be skipped: {unmappedFields.join(', ')}
          </div>
        )}
      </div>

      <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm}>
          Confirm Mapping
        </Button>
      </div>
    </Modal>
  );
};

export default FieldMappingModal;

